import { CalendarDays, Filter, Phone, Table2 } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { CONTACT_FILTERS, CONTACT_FILTER_LABELS, type JobDetail } from '@nest-recruit/shared';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { registerCommands } from '../../../shell/command-registry';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useViewerZone } from '../../interviews/hooks/use-interview-actions';
import { validatePipelineSearch, type ContactView, type PipelineSearch } from '../../pipeline/pipeline-search';
import { useContactTracker, usePlanner } from '../api/contact-api';
import { ContactTracker } from './ContactTracker';
import { PlannerCalendar } from './PlannerCalendar';
import { InterviewPanel, PlannerTable } from './PlannerTable';
import styles from './contact.module.css';
import fit from '../../../components/FitPage.module.css';

// R7 — stage ⑥ Contact on JOB-12: "two views of the approved candidates" (PRD §4.6), the
// tracker (CON-01) and the interview planner (PLN-01 table, PLN-02 calendar), one switch
// between them (DECISIONS-LOG "Start R7" Q2). The view, filter, week and open interview are in
// the address, so a link reopens exactly this (06-qol §6).

interface StageProps {
  readonly detail: JobDetail;
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
}

const VIEW_OPTIONS: readonly { readonly value: ContactView; readonly label: string }[] = [
  { value: 'tracker', label: 'Tracker' },
  { value: 'planner', label: 'Planner' },
  { value: 'calendar', label: 'Calendar' },
];

export function ContactStage({ detail, search, onSearch }: StageProps): ReactNode {
  const view = search.cview ?? 'tracker';
  const update = (changes: Partial<PipelineSearch>): void => onSearch(validatePipelineSearch({ ...search, ...changes }));
  const title = detail.publicTitle ?? detail.title;
  // 06-qol §1: the stage's views and O34's questions, one palette search away while it is open.
  useEffect(() => {
    const go = (changes: Partial<PipelineSearch>): void => onSearch(validatePipelineSearch({ ...search, ...changes }));
    return registerCommands([
      ...VIEW_OPTIONS.map((option) => ({
        id: `contact.view.${detail.id}.${option.value}`,
        label: option.value === 'tracker' ? 'Open the contact tracker' : `Open the interview ${option.value}`,
        section: 'action' as const,
        context: title,
        permission: 'application' as const,
        icon: option.value === 'calendar' ? CalendarDays : option.value === 'planner' ? Table2 : Phone,
        keywords: ['contact', 'interview', 'plan', option.label.toLowerCase()],
        run: () => go({ cview: option.value, iv: undefined, app: undefined }),
      })),
      ...CONTACT_FILTERS.filter((filter) => filter !== 'all').map((filter) => ({
        id: `contact.filter.${detail.id}.${filter}`,
        label: `Contact: ${CONTACT_FILTER_LABELS[filter].toLowerCase()}`,
        section: 'action' as const,
        context: title,
        permission: 'application' as const,
        icon: Filter,
        keywords: ['who', 'emailing', 'calling', 'coming', 'contact'],
        run: () => go({ cview: undefined, cfilter: filter }),
      })),
    ]);
  }, [detail.id, title, search, onSearch]);
  return (
    <div className={`${styles.stage} ${fit.fill}`}>
      <div className={styles.switch}>
        <Segmented label="View" value={view} options={VIEW_OPTIONS} onChange={(next) => update({ cview: next, iv: undefined, app: undefined })} />
      </div>
      {view === 'tracker' ? <TrackerView detail={detail} search={search} onSearch={onSearch} /> : <PlannerViews detail={detail} search={search} onSearch={onSearch} />}
    </div>
  );
}

function TrackerView({ detail, search, onSearch }: StageProps): ReactNode {
  const tracker = useContactTracker(detail.id);
  const zone = useViewerZone();
  if (tracker.isPending) return <SkeletonLines count={6} />;
  if (tracker.error?.status === 403) return <ForbiddenState resource="this contact list" contact="Your admin" isInset />;
  if (tracker.error || !tracker.data) return <ServerErrorState onRetry={() => void tracker.refetch()} />;
  return (
    <ContactTracker
      detail={detail}
      view={tracker.data}
      zone={zone}
      filter={search.cfilter ?? 'all'}
      onFilter={(cfilter) => onSearch(validatePipelineSearch({ ...search, cfilter }))}
      // Redesign Group 4: the person's pop-up is in the address as Ranking's "Why this score" is (`app`).
      openApplicationId={search.app ?? null}
      onOpenCandidate={(app) => onSearch(validatePipelineSearch({ ...search, app: app ?? undefined }))}
    />
  );
}

function PlannerViews({ detail, search, onSearch }: StageProps): ReactNode {
  const planner = usePlanner(detail.id);
  const zone = useViewerZone();
  const update = (changes: Partial<PipelineSearch>): void => onSearch(validatePipelineSearch({ ...search, ...changes }));
  if (planner.isPending) return <SkeletonLines count={6} />;
  if (planner.error?.status === 403) return <ForbiddenState resource="this job's interviews" contact="Your admin" isInset />;
  if (planner.error || !planner.data) return <ServerErrorState onRetry={() => void planner.refetch()} />;
  if (search.cview === 'calendar') {
    return (
      <>
        <PlannerCalendar
          jobId={detail.id}
          view={planner.data}
          zone={zone}
          anchorDay={search.cweek ?? null}
          span={search.cspan === 'day' ? 'day' : 'week'}
          onNavigate={(cweek, span) => update({ cweek: cweek ?? undefined, cspan: span === 'day' ? 'day' : undefined })}
          onOpen={(iv) => update({ iv })}
        />
        <InterviewPanel interviewId={search.iv ?? null} zone={zone} onClose={() => update({ iv: undefined })} />
      </>
    );
  }
  return <PlannerTable jobId={detail.id} view={planner.data} zone={zone} openId={search.iv ?? null} onOpen={(iv) => update({ iv: iv ?? undefined })} />;
}
